import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  limit,
  query,
  runTransaction,
  where,
} from "firebase/firestore";
import toast from "react-hot-toast";
import { db } from "../../firebase";
import { buildOmiMatchState } from "./omiUtils";

export default function OmiJoinRoomModal({
  open,
  onClose,
  user,
  setCurrentRoomId,
}) {
  const [roomCode, setRoomCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [roomPreview, setRoomPreview] = useState(null);

  const resetModal = () => {
    if (joining) return;
    setRoomCode("");
    setRoomPreview(null);
    onClose();
  };

  async function lookupRoom(code) {
    const snap = await getDocs(
      query(
        collection(db, "omi_rooms"),
        where("roomId", "==", code.trim().toUpperCase()),
        limit(1),
      ),
    );
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ref: snap.docs[0].ref, ...snap.docs[0].data() };
  }

  const handleRoomCodeChange = async (value) => {
    setRoomCode(value);
    if (value.trim().length < 4) {
      setRoomPreview(null);
      return;
    }
    try {
      const room = await lookupRoom(value);
      setRoomPreview(room);
    } catch {
      setRoomPreview(null);
    }
  };

  const handleJoin = async () => {
    if (!user?.uid) {
      sessionStorage.setItem("postLoginRedirect", "/omi");
      toast.error("Please log in to join an Omi table.");
      return;
    }
    if (!roomCode.trim()) {
      toast.error("Enter the room code.");
      return;
    }

    setJoining(true);
    try {
      const room = await lookupRoom(roomCode);
      if (!room) throw new Error("Room not found.");

      const roomRef = doc(db, "omi_rooms", room.id);

      await runTransaction(db, async (transaction) => {
        const roomSnap = await transaction.get(roomRef);
        const userRef = doc(db, "users", user.uid);
        const userSnap = await transaction.get(userRef);

        if (!roomSnap.exists()) throw new Error("Room not found.");
        if (!userSnap.exists()) throw new Error("User not found.");

        const roomData = roomSnap.data() || {};
        const players = roomData.players || [];
        const betAmount = Number(roomData.betAmount || 1);
        const balance = Number(userSnap.data()?.balance || 0);

        if (players.some((player) => player.userId === user.uid)) {
          return;
        }
        if (players.length >= 4) throw new Error("Room already full.");
        if (roomData.status !== "waiting") throw new Error("Game already started.");
        if (balance < betAmount) throw new Error("Insufficient balance.");

        const nextPlayers = [
          ...players,
          {
            userId: user.uid,
            username: user.displayName || user.email || "Player",
            avatar: user.photoURL || "",
            seat: players.length + 1,
            joinedAt: new Date(),
          },
        ];

        transaction.update(userRef, {
          balance: Number((balance - betAmount).toFixed(2)),
          activeOmiRoomId: roomRef.id,
        });

        transaction.update(roomRef, {
          players: arrayUnion(nextPlayers[nextPlayers.length - 1]),
        });

        if (nextPlayers.length === 4) {
          const matchState = buildOmiMatchState(nextPlayers);
          transaction.update(roomRef, {
            players: matchState.players,
            status: matchState.status,
            turnOrder: matchState.turnOrder,
            turnUserId: matchState.turnUserId,
            hands: matchState.hands,
            trick: matchState.trick,
            lastTrick: matchState.lastTrick,
            scores: matchState.scores,
            trump: matchState.trump,
            round: matchState.round,
            trickNumber: matchState.trickNumber,
            winnerTeam: matchState.winnerTeam,
            payoutProcessed: matchState.payoutProcessed,
            rematchReady: matchState.rematchReady,
            finishedReason: matchState.finishedReason,
            forfeitedBy: matchState.forfeitedBy,
          });
        }
      });

      setCurrentRoomId(room.id);
      toast.success("Joined Omi table.");
      resetModal();
    } catch (error) {
      toast.error(error?.message || "Failed to join room.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="w-full max-w-md rounded-[28px] border border-emerald-400/30 bg-[#131923] p-6 shadow-[0_30px_90px_rgba(0,0,0,0.45)]"
            initial={{ scale: 0.96, y: 18 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 18 }}
          >
            <div className="text-xs uppercase tracking-[0.24em] text-emerald-300">
              Omi
            </div>
            <h3 className="mt-2 text-3xl font-black text-white">Join Table</h3>
            <p className="mt-3 text-sm leading-7 text-slate-400">
              Enter the room code shared by the table creator and grab an empty seat.
            </p>

            <label className="mt-6 block">
              <div className="mb-2 text-xs uppercase tracking-[0.24em] text-slate-500">
                Table Code
              </div>
              <input
                type="text"
                value={roomCode}
                onChange={(event) => handleRoomCodeChange(event.target.value)}
                placeholder="OMI-XXXX"
                className="w-full rounded-[18px] bg-[#1c2330] px-4 py-4 text-center text-lg font-black uppercase tracking-[0.18em] text-white outline-none"
              />
            </label>

            {roomPreview ? (
              <div className="mt-4 rounded-[20px] bg-[#1c2330] p-4 text-sm text-slate-300">
                <div className="font-bold text-white">{roomPreview.roomId}</div>
                <div className="mt-2">Entry fee: {Number(roomPreview.betAmount || 0).toFixed(2)} USDT</div>
                <div className="mt-1">Players: {(roomPreview.players || []).length} / 4</div>
              </div>
            ) : null}

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={resetModal}
                disabled={joining}
                className="rounded-[18px] border border-white/10 bg-white/[0.04] px-4 py-3 font-semibold text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleJoin}
                disabled={joining || !roomCode.trim()}
                className="rounded-[18px] bg-emerald-400 px-4 py-3 font-black text-[#05120d] disabled:opacity-60"
              >
                {joining ? "Joining..." : "Join Table"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
